const PROBE_KEY = '__hb_storage_probe__';
/**
 * Takes a function because reading the storage property itself can throw.
 * `removeItem` on a never-written key is the usability probe.
 */
function usableStorage(read) {
    try {
        const storage = read();
        storage.removeItem(PROBE_KEY);
        return storage;
    }
    catch {
        return null;
    }
}
/**
 * `localStorage`, else `sessionStorage`, else none. Methods degrade instead of
 * throwing: reads return `null`, writes are no-ops.
 */
export class LocalStorageService {
    getStorage() {
        if (this.resolvedStorage === undefined) {
            this.resolvedStorage =
                usableStorage(() => localStorage) ??
                    usableStorage(() => sessionStorage);
        }
        return this.resolvedStorage;
    }
    async get(key) {
        try {
            return this.getStorage()?.getItem(key) ?? null;
        }
        catch (error) {
            console.warn('LocalStorageService.get failed:', error);
            return null;
        }
    }
    /** Returns `null` for a missing key or a non-JSON value. */
    async getObject(key) {
        try {
            const value = await this.get(key);
            if (value === null) {
                return null;
            }
            return JSON.parse(value);
        }
        catch (error) {
            // Handle JSON parse errors
            console.warn('LocalStorageService.getObject failed:', error);
            return null;
        }
    }
    async set(key, value) {
        try {
            this.getStorage()?.setItem(key, value);
        }
        catch (error) {
            // Quota exceeded, or storage revoked since it was resolved.
            console.warn('LocalStorageService.set failed:', error);
            // Don't throw - graceful degradation
        }
    }
    async setObject(key, value) {
        try {
            const serializedValue = JSON.stringify(value);
            await this.set(key, serializedValue);
        }
        catch (error) {
            // Handle JSON stringify errors
            console.warn('LocalStorageService.setObject failed:', error);
            // Don't throw - graceful degradation
        }
    }
    async remove(key) {
        try {
            this.getStorage()?.removeItem(key);
        }
        catch (error) {
            console.warn('LocalStorageService.remove failed:', error);
            // Don't throw - graceful degradation
        }
    }
}
//# sourceMappingURL=local-storage.service.js.map